import { Trash2 } from "lucide-react";
import { type ChangeEvent, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";

import {
  Button,
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "~/components/ui";
import { v2Route } from "~/v2/routes";

const MAX_FILES = 3;

interface RecordGeoItemCreateWithFileDialogProps {
  open: boolean;
  onOpenChange: (next: boolean) => void;
  recordId: string;
}

/** Upload up to 3 H3-index JSON files; their cells seed a new create form. */
export function RecordGeoItemCreateWithFileDialog({
  open,
  onOpenChange,
  recordId,
}: RecordGeoItemCreateWithFileDialogProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [files, setFiles] = useState<File[]>([]);
  const [error, setError] = useState<string | null>(null);

  const reset = () => {
    setFiles([]);
    setError(null);
  };

  const onSelect = (e: ChangeEvent<HTMLInputElement>) => {
    setError(null);
    const picked = Array.from(e.target.files ?? []);
    e.target.value = "";
    if (files.length + picked.length > MAX_FILES) {
      setError(t("feature.recordGeoItems.createWithH3IndexesFileDialog.tooManyFiles", { max: MAX_FILES }));
      return;
    }
    setFiles((prev) => [...prev, ...picked]);
  };

  const onSubmit = () => {
    if (files.length === 0) {
      setError(t("feature.recordGeoItems.createWithH3IndexesFileDialog.noFiles"));
      return;
    }
    const h3IndexesFileURLs = files.map((file) => window.URL.createObjectURL(file));
    onOpenChange(false);
    reset();
    navigate(v2Route.areaInput.record.create.$buildPath({ params: { recordId }, searchParams: { h3IndexesFileURLs } }));
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) reset();
        onOpenChange(next);
      }}
    >
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="capitalize">
            {t("feature.recordGeoItems.createWithH3IndexesFileDialog.title")}
          </DialogTitle>
          <DialogDescription>{t("feature.recordGeoItems.createWithH3IndexesFileDialog.content")}</DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <input
            type="file"
            accept=".json"
            multiple
            onChange={onSelect}
            disabled={files.length >= MAX_FILES}
            className="block w-full text-[13px] text-fg-2 file:mr-3 file:rounded-[var(--radius-sm)] file:border file:border-border-default file:bg-bg-subtle file:px-3 file:py-1.5 file:text-fg-1"
          />
          <p className="text-12-regular text-fg-3">
            {t("feature.recordGeoItems.createWithH3IndexesFileDialog.maxFiles", { max: MAX_FILES })}
          </p>
          {files.length > 0 && (
            <ul className="space-y-1">
              {files.map((file, index) => (
                <li key={`${file.name}-${index}`} className="flex items-center justify-between gap-2 text-[13px]">
                  <span className="truncate text-fg-1">{file.name}</span>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-6 w-6"
                    aria-label={t("ui.delete")}
                    onClick={() => setFiles((prev) => prev.filter((_, i) => i !== index))}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </li>
              ))}
            </ul>
          )}
          {error && <p className="text-12-medium text-danger">{error}</p>}
        </div>

        <DialogFooter>
          <DialogClose asChild>
            <Button type="button" variant="outline">
              {t("ui.cancel")}
            </Button>
          </DialogClose>
          <Button type="button" onClick={onSubmit} disabled={files.length === 0}>
            {t("ui.submit")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
